import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import Button from '~/app/common/Button';
import File from '~/app/modules/form/File';
import { AttachmentsValue } from '~/training/types';
import classes from './style.module.scss';
import { useStoreState } from '~/store';
import { buildAcceptFromExtensions, normalizeExtensions, getExtensionFromName } from '~/messages/attachmentUtils';

interface AttachmentsProps {
  onChange: (value: AttachmentsValue) => void;
}

export type AttachmentsComponent = React.FC<AttachmentsProps>;

const Attachments: AttachmentsComponent = ({ onChange }) => {
  const { t } = useTranslation('training');

  const { maxImageSize, maxNumberOfImages, maxPDFSize, maxNumberOfPDFs } = useStoreState(state => state.machines?.trainingAttachmentLimit);
  const allowedTrainingFileExtensions = useStoreState(state => state.machines.allowedTrainingFileExtensions);

  const pdfAccept = React.useMemo(
    () => buildAcceptFromExtensions(allowedTrainingFileExtensions),
    [allowedTrainingFileExtensions],
  );

  const allowedExtensionsNormalized = React.useMemo(
    () => normalizeExtensions(allowedTrainingFileExtensions),
    [allowedTrainingFileExtensions],
  );

  const [images, setImages] = useState<any[]>([]);
  const [pdfs, setPdfs] = useState<any[]>([]);

  const imageInputRef = useRef<any>(null);
  const pdfInputRef = useRef<any>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => {
    setIsExpanded(prevState => !prevState);
  };

  const handleImageChange = useCallback((e: any) => {
    const newFiles: File[] = e.target.value;
    const maxImageSizeBytes = maxImageSize * 1024 * 1024;
    const oversizedIndex = newFiles.findIndex(f => f.size > maxImageSizeBytes);
    if (oversizedIndex !== -1) {
      imageInputRef.current?.handleImageDelete(oversizedIndex);
      toast.error(t('attachments.sizeError', { max: maxImageSize }));
      return;
    }
    setImages(newFiles);
    onChange({ images: newFiles, pdfs });
  }, [pdfs, onChange, t, maxImageSize]);

  const handlePdfChange = useCallback((e: any) => {
    const newFiles: File[] = e.target.value;

    const maxPDFSizeBytes = maxPDFSize * 1024 * 1024;
    const oversizedIndex = newFiles.findIndex(f => f.size > maxPDFSizeBytes);
    if (oversizedIndex !== -1) {
      pdfInputRef.current?.handleImageDelete(oversizedIndex);
      toast.error(t('attachments.sizeError', { max: maxPDFSize }));
      return;
    }

    if (allowedExtensionsNormalized.length > 0) {
      const unsupportedIndex = newFiles.findIndex(f => {
        const ext = getExtensionFromName(f.name);
        return !ext || !allowedExtensionsNormalized.includes(ext);
      });
      if (unsupportedIndex !== -1) {
        pdfInputRef.current?.handleImageDelete(unsupportedIndex);
        toast.error(t('attachments.typeError'));
        return;
      }
    }

    setPdfs(newFiles);
    onChange({ images, pdfs: newFiles });
  }, [images, onChange, t, maxPDFSize, allowedExtensionsNormalized]);

  return (
    <>
      <div onClick={toggleExpand} className={classes['section']}>
        {t('attachments.title')}
        {isExpanded ? <Icon name='keyboard_arrow_up' /> : <Icon name='keyboard_arrow_down' />}
      </div>

      {isExpanded && (
        <>
          {/* Images */}
          <div className={classes['attachment-row']}>
            <div className={classes['attachment-label']}>
              <Icon name="photo_camera" />
              <span>{t('attachments.images')} {t('attachments.range', { current: images.length, max: maxNumberOfImages })}</span>
            </div>
            <Button
              className={classes['upload-btn']}
              type="button"
              disabled={images.length === maxNumberOfImages}
              onClick={(e) => imageInputRef.current?.triggerFileInputClick(e)}
            >
              <Icon name="upload" />
              {t('attachments.upload')}
            </Button>
          </div>
          <div className={classes['attachment-hint']}>{t('attachments.imageHint', { max: maxImageSize })}</div>

          <File
            ref={imageInputRef}
            name="images"
            maxFiles={maxNumberOfImages}
            preview
            showInput={false}
            accept="image/*"
            onChange={handleImageChange}
          />

          <hr className={classes['attachment-divider']} />

          {/* PDFs */}
          <div className={classes['attachment-row']}>
            <div className={classes['attachment-label']}>
              <Icon name="description" />
              <span>{t('attachments.pdfs')} {t('attachments.range', { current: pdfs.length, max: maxNumberOfPDFs })}</span>
            </div>
            <Button
              className={classes['upload-btn']}
              type="button"
              disabled={pdfs.length === maxNumberOfPDFs}
              onClick={(e) => pdfInputRef.current?.triggerFileInputClick(e)}
            >
              <Icon name="upload" />
              {t('attachments.upload')}
            </Button>
          </div>
          <div className={classes['attachment-hint']}>{t('attachments.pdfHint', { max: maxPDFSize })}</div>

          <File
            ref={pdfInputRef}
            name="pdfs"
            maxFiles={maxNumberOfPDFs}
            preview={false}
            showInput={false}
            accept={pdfAccept || 'application/pdf'}
            onlyImage={false}
            imageEdit={false}
            onChange={handlePdfChange}
          />

          {/* PDF Preview */}
          {pdfs.length > 0 && (
            <div className={classes['pdf-preview-list']}>
              {pdfs.map((file, index) => (
                <div key={index} className={classes['pdf-preview-item']}>
                  <Icon name="description" className={classes['pdf-icon']} />
                  <div className={classes['pdf-info']}>
                    <div className={classes['pdf-name']}>{file.name}</div>
                    <div className={classes['pdf-size']}>{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <Icon
                    name="close"
                    className={classes['pdf-remove']}
                    onClick={() => pdfInputRef.current?.handleImageDelete(index)}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Attachments;